import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { MobileNavigation } from '../components/MobileNavigation';
import { 
  FirstAid, 
  Warning, 
  ClipboardText, 
  PaperPlaneTilt,
  Phone,
  MapPin,
  Shield,
  Users,
  CheckCircle,
  XCircle,
  Basketball,
  ArrowLeft,
  DownloadSimple
} from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/api';

export function StaffResources() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('staff');
  const [activeTab, setActiveTab] = useState('eap');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Incident Report Form State
  const [incidentForm, setIncidentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '',
    playerNames: '',
    parentContact: '',
    description: '',
    actionTaken: '',
    staffWitness: '',
    followUpNeeded: false,
    reportedBy: ''
  });
  
  React.useEffect(() => {
    fetchUserRole();
  }, []);
  
  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role || 'staff');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setIncidentForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitIncident = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!incidentForm.location || !incidentForm.playerNames || !incidentForm.description || !incidentForm.reportedBy) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/staff/incident-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(incidentForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit incident report');
      }
      
      toast.success('Incident report submitted and sent to info@stayonhoops.com');
      
      // Reset form
      setIncidentForm({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        location: '',
        playerNames: '',
        parentContact: '',
        description: '',
        actionTaken: '',
        staffWitness: '',
        followUpNeeded: false,
        reportedBy: ''
      });
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDownloadQuickReference = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/staff/quick-reference-pdf`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download Quick Reference Card');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'StayOnHoops_QuickReference.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Quick Reference Card downloaded!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="staff-resources-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft size={24} />
            </Button>
            <div>
              <h1 className="text-xl font-heading">STAFF RESOURCES</h1>
              <p className="text-sm text-muted-foreground">EAP, Quick Sheet & Incident Reports</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'eap' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('eap')}
            className="whitespace-nowrap"
            data-testid="tab-eap"
          >
            <FirstAid size={16} className="mr-1" />
            Emergency Plan
          </Button>
          <Button
            variant={activeTab === 'quicksheet' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('quicksheet')}
            className="whitespace-nowrap"
            data-testid="tab-quicksheet"
          >
            <ClipboardText size={16} className="mr-1" />
            Quick Sheet
          </Button>
          <Button
            variant={activeTab === 'incident' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('incident')}
            className="whitespace-nowrap"
            data-testid="tab-incident"
          >
            <Warning size={16} className="mr-1" />
            Incident Report
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 pb-6">
        {/* Emergency Action Plan */}
        {activeTab === 'eap' && (
          <div className="space-y-4" data-testid="eap-content">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-red-500">
                  <FirstAid size={24} weight="fill" />
                  EMERGENCY ACTION PLAN (EAP)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="text-sm text-muted-foreground">Program: Stay On Hoops (Product of Intuitive Mentors Organization)</p>
                  <p className="text-lg font-bold text-red-500 flex items-center gap-2 mt-2">
                    <Phone size={20} weight="fill" />
                    Emergency Number: 911
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading text-base mb-3 text-red-400">IN CASE OF INJURY OR EMERGENCY:</h3>
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3 bg-background/30 p-3 rounded-lg">
                      <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                      <span className="font-medium">Stop play immediately.</span>
                    </li>
                    <li className="flex items-start gap-3 bg-background/30 p-3 rounded-lg">
                      <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                      <span><span className="font-medium">Staff Member #1</span> calls 911 if needed.</span>
                    </li>
                    <li className="flex items-start gap-3 bg-background/30 p-3 rounded-lg">
                      <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                      <span><span className="font-medium">Staff Member #2</span> contacts parent/guardian.</span>
                    </li>
                    <li className="flex items-start gap-3 bg-background/30 p-3 rounded-lg">
                      <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                      <span><span className="font-medium">Staff Member #3</span> clears court area.</span>
                    </li>
                    <li className="flex items-start gap-3 bg-background/30 p-3 rounded-lg">
                      <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                      <span>Provide basic first aid until help arrives.</span>
                    </li>
                  </ol>
                </div>
                
                <div className="grid gap-3">
                  <div className="bg-background/30 p-3 rounded-lg flex items-center gap-2">
                    <FirstAid size={20} className="text-green-500" />
                    <span className="text-sm"><strong>First Aid Kit Location:</strong> Check with facility staff</span>
                  </div>
                  <div className="bg-background/30 p-3 rounded-lg flex items-center gap-2">
                    <Shield size={20} className="text-yellow-500" />
                    <span className="text-sm"><strong>AED Location (if available):</strong> Check with facility staff</span>
                  </div>
                  <div className="bg-background/30 p-3 rounded-lg flex items-center gap-2">
                    <MapPin size={20} className="text-blue-500" />
                    <span className="text-sm"><strong>Nearest Hospital:</strong> Check facility emergency info</span>
                  </div>
                </div>
                
                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                    <Warning size={18} />
                    Incident Documentation Required After Any Event
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Staff Quick Sheet */}
        {activeTab === 'quicksheet' && (
          <div className="space-y-4" data-testid="quicksheet-content">
            <Card className="bg-primary/10 border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-primary">
                  <ClipboardText size={24} weight="fill" />
                  STAFF QUICK SHEET
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="font-medium text-primary">Your Role: Safety, flow, and structure.</p>
                  <p className="text-sm text-muted-foreground mt-1">No coaching or refereeing.</p>
                </div>
                
                {/* Check-In Section */}
                <div>
                  <h3 className="font-heading text-base mb-2 flex items-center gap-2">
                    <CheckCircle size={18} className="text-green-500" />
                    Check-In
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Verify waiver
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Check player in via QR/admin
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Late arrivals wait next rotation
                    </li>
                  </ul>
                </div>
                
                {/* Game Rules Section */}
                <div>
                  <h3 className="font-heading text-base mb-2 flex items-center gap-2">
                    <Basketball size={18} className="text-primary" />
                    Game Rules
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      First game to 13 (2s & 1s)
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Games after to 11 (1s only)
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Timed games
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Winners stay on
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Losing team goes to bottom
                    </li>
                  </ul>
                </div>
                
                {/* Fouls Section */}
                <div>
                  <h3 className="font-heading text-base mb-2 flex items-center gap-2">
                    <Users size={18} className="text-yellow-500" />
                    Fouls
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Defense calls fouls
                    </li>
                    <li className="flex items-start gap-2 bg-background/30 p-2 rounded">
                      <span className="text-primary">•</span>
                      Questionable = shoot for it
                    </li>
                  </ul>
                </div>
                
                {/* Remove Player Section */}
                <div>
                  <h3 className="font-heading text-base mb-2 flex items-center gap-2">
                    <XCircle size={18} className="text-red-500" />
                    Remove Player For
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2 bg-red-500/10 p-2 rounded border border-red-500/20">
                      <span className="text-red-500">•</span>
                      Dangerous play
                    </li>
                    <li className="flex items-start gap-2 bg-red-500/10 p-2 rounded border border-red-500/20">
                      <span className="text-red-500">•</span>
                      Fighting
                    </li>
                    <li className="flex items-start gap-2 bg-red-500/10 p-2 rounded border border-red-500/20">
                      <span className="text-red-500">•</span>
                      Disrespect
                    </li>
                  </ul>
                </div>
                
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-400 flex items-center gap-2">
                    <Warning size={18} />
                    Emergencies: Follow EAP immediately.
                  </p>
                </div>
                
                {/* Download Quick Reference Card */}
                <Button
                  onClick={handleDownloadQuickReference}
                  disabled={downloading}
                  className="w-full mt-4 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30"
                  variant="outline"
                  data-testid="download-quick-reference-btn"
                >
                  {downloading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <DownloadSimple size={18} className="mr-2" />
                      Download Printable Pocket Guide (PDF)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Incident Report Form */}
        {activeTab === 'incident' && (
          <div className="space-y-4" data-testid="incident-content">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Warning size={24} className="text-yellow-500" />
                  INCIDENT REPORT FORM
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Complete this form after any incident. Report will be sent to info@stayonhoops.com
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitIncident} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        value={incidentForm.date}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        data-testid="incident-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="time">Time *</Label>
                      <Input
                        id="time"
                        name="time"
                        type="time"
                        value={incidentForm.time}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        data-testid="incident-time"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={incidentForm.location}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Downtown Community Gym"
                      className="mt-1"
                      data-testid="incident-location"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="playerNames">Player Name(s) Involved *</Label>
                    <Input
                      id="playerNames"
                      name="playerNames"
                      value={incidentForm.playerNames}
                      onChange={handleInputChange}
                      required
                      placeholder="Names of players involved"
                      className="mt-1"
                      data-testid="incident-players"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="parentContact">Parent/Guardian Contact</Label>
                    <Input
                      id="parentContact"
                      name="parentContact"
                      value={incidentForm.parentContact}
                      onChange={handleInputChange}
                      placeholder="Phone number or email"
                      className="mt-1"
                      data-testid="incident-parent-contact"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description of Incident *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={incidentForm.description}
                      onChange={handleInputChange}
                      required
                      placeholder="Describe what happened in detail..."
                      className="mt-1 min-h-[100px]"
                      data-testid="incident-description"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="actionTaken">Action Taken</Label>
                    <Textarea
                      id="actionTaken"
                      name="actionTaken"
                      value={incidentForm.actionTaken}
                      onChange={handleInputChange}
                      placeholder="What steps were taken to address the incident..."
                      className="mt-1 min-h-[80px]"
                      data-testid="incident-action"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="staffWitness">Staff Witness</Label>
                    <Input
                      id="staffWitness"
                      name="staffWitness"
                      value={incidentForm.staffWitness}
                      onChange={handleInputChange}
                      placeholder="Name of staff witness (if any)"
                      className="mt-1"
                      data-testid="incident-witness"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="followUpNeeded"
                      checked={incidentForm.followUpNeeded}
                      onCheckedChange={(checked) => 
                        setIncidentForm(prev => ({ ...prev, followUpNeeded: checked }))
                      }
                      data-testid="incident-followup"
                    />
                    <Label htmlFor="followUpNeeded" className="text-sm cursor-pointer">
                      Follow-Up Needed
                    </Label>
                  </div>
                  
                  <div>
                    <Label htmlFor="reportedBy">Reported By (Your Name) *</Label>
                    <Input
                      id="reportedBy"
                      name="reportedBy"
                      value={incidentForm.reportedBy}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                      className="mt-1"
                      data-testid="incident-reported-by"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary"
                    data-testid="submit-incident-btn"
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={18} className="mr-2" />
                        Submit Incident Report
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      <MobileNavigation userRole={userRole} />
    </div>
  );
}
